import React, { useRef, useEffect } from "react"
import PropTypes from "prop-types"
import styled from "styled-components"
import { motion, useAnimation } from "framer-motion"
import { GatsbyImage, getImage } from "gatsby-plugin-image"

import { useOnScreen } from "hooks/"
import ContentWrapper from "styles/contentWrapper"


const circleSmall = "https://hrflow-ai.imgix.net/backgrounds/circlesmall.svg"
const circleBig = "https://hrflow-ai.imgix.net/backgrounds/circlebig.svg"

const StyledSection = styled.section`
  width: 100%;
  height: auto;
  background: ${({ theme }) => theme.colors.white};
  padding: 3rem 0;
  .more {
    width: 100%;
    display: flex;
    justify-content: center;
    padding: 3rem 0 0 0;
  }
`

const StyledContentWrapper = styled(ContentWrapper)`
  && {
    width: 100%;
    height: 100%;
    display: flex;
    flex-direction: column;
    justify-content: center;
    padding-right: 0;
    padding-left: 0;
    @media (min-width: ${({ theme }) => theme.breakpoints.sm}) {
      padding-right: 2.5rem;
      padding-left: 2.5rem;
    }
  }
`

const StyledFlex = styled.div`
  display: flex;
  justify-content: center;
  align-items: flex-start;
  max-width: 50rem;
  flex-wrap: wrap;
  margin: auto;
  margin-bottom: 3rem;
  div {
    margin: 1rem;
    text-align: center;
  }
  @media (min-width: ${({ theme }) => theme.breakpoints.sm}) {
    div {
      max-width: 80%;
    }
  }
`

const StyledLayers = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(25rem, 1fr));
  grid-gap: 2rem;
  grid-auto-rows: minmax(220px, auto);
  justify-items: center;
  .item {
    .logo-wrapper {
      width: 6.359rem;
      height: 5.1rem;
      margin-bottom: 1.2rem;
      background-image: url(${circleSmall}), url(${circleBig});
      background-size: 2.1rem 2.1rem, 3rem 3rem;
      background-repeat: no-repeat;
      background-position: 88% 9%, 0 100%;
      display: flex;
      align-items: center;
      justify-content: center;
      .logo {
        width: 50px;
        height: auto;
      }
    }

    h3 {
      margin: 0;
      margin: 0.75rem 0;
      font-size: 1.3rem;
      max-width: 20rem;
    }
    .text {
      max-width: 20rem;
      min-width: 20rem;
    }
  }
  @media (max-width: ${({ theme }) => theme.breakpoints.md}) {
    grid-auto-rows: auto;
  }
  @media (max-width: ${({ theme }) => theme.breakpoints.xs}) {
    grid-template-columns: repeat(1, 1fr);
    .item {
      max-width: 90%;
    }
  }
`

const StyledLargeButton = styled.a`
  padding: 3rem 0;
  margin: 3rem auto;
  background-color: #fff;
  border-color: #fff;
  border: .1rem solid ${({ theme }) => theme.colors.secondary};
  color: ${({ theme }) => theme.colors.secondary};
  font-size: 0.9rem;
  font-weight: 700;
  font-style: normal;
  font-stretch: normal;
  line-height: normal;
  letter-spacing: .065rem;
  padding: 1.5rem;
  border-radius: 0.25rem;
  cursor: pointer;
  text-decoration: none;
  text-align: center;
  text-align: center;
`


const Layers = ({ content }) => {
  const { exports, frontmatter } = content[0].node
  const { items } = exports
  const ref = useRef()
  const onScreen = useOnScreen(ref)

  const iControls = useAnimation()
  const bControls = useAnimation()

  useEffect(() => {
    const sequence = async () => {
      if (onScreen) {
        // i receives the value of the custom prop - can be used to stagger
        // the animation of each "interest" element
        await iControls.start(i => ({
          opacity: 1,
          scaleY: 1,
          transition: { delay: i * 0.1 },
        }))
      }
    }
    sequence()
  }, [onScreen, iControls])

  return (
    <StyledSection id="layers" ref={ref}>
      <StyledContentWrapper>
        <h2 className="section-title">{frontmatter.title}</h2>
        <StyledFlex>
        </StyledFlex>
        <StyledLayers>
          {items.map(({ title, logo, description, link}, key) => {
            const image = getImage(logo)
            return (
              <motion.div
                className="item"
                key={key}
                custom={key}
                initial={{ opacity: 0, scaleY: 0 }}
                animate={iControls}
              >
                <div className="logo-wrapper">
                  <GatsbyImage className="logo" alt={title} image={image} />
                </div>
                <h3>{title}</h3>
                <div className="text mb-075">{description}{" "}{link && <a href={link}>See more </a>}</div>
              </motion.div>
            )
          })}
        </StyledLayers>
      </StyledContentWrapper>
      <div className="more">
        <StyledLargeButton href="https://help.hrflow.ai/">MORE QUESTIONS?</StyledLargeButton>
      </div>
    </StyledSection>
  )
}

Layers.propTypes = {
  content: PropTypes.arrayOf(
    PropTypes.shape({
      node: PropTypes.shape({
        exports: PropTypes.shape({
          items: PropTypes.array.isRequired,
          shownItems: PropTypes.number.isRequired,
        }).isRequired,
        frontmatter: PropTypes.object.isRequired,
      }).isRequired,
    }).isRequired
  ).isRequired,
}

export default Layers
